import 'src/content/Products/ProductList.scss'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { faFileExcel, faBoxOpen } from '@fortawesome/free-solid-svg-icons'

import { Card, Select, Title, Dropdown } from 'src/components'
import WarehouseInventoryComponent from 'src/content/Warehouse/WarehouseInventory'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { actionTypes, getWarehouseLite } from 'src/actions/warehouse.actions'
import { onSetLocalCategories } from 'src/actions/categorization.actions'
import {
  selectOwnWarehouseLite,
  selectWarehouseLite,
} from 'src/selectors/warehouse.selector'
import { loadingSelector } from 'src/selectors/loading.selector'
import { isAllowed, selectCurrentUserRoles } from 'src/selectors/modules.selector'
import { inventoriesPermissions } from 'src/enums/permissions'

import { useInventoryExport, InventoryExportParams } from './useInventoryExport'
import { useReservedInventory } from './useReservedInventory'
import ReservedInventoryModal from './ReservedInventoryModal'

interface WarehouseOption {
  label: string
  value: number | null
}

const DEFAULT_WAREHOUSE: WarehouseOption = { label: '- No seleccionado -', value: null }

// Componente legacy en JS: se relaja el tipado de props en el borde de interop.
const WarehouseInventory = WarehouseInventoryComponent as unknown as React.ComponentType<{
  id: number | null
  isInventory?: boolean
  onChangeParams?: (params: InventoryExportParams) => void
}>

const Inventory = () => {
  const dispatch = useAppDispatch()

  const allWarehouses = useAppSelector(selectWarehouseLite) as WarehouseOption[]
  const ownWarehouses = useAppSelector(selectOwnWarehouseLite) as WarehouseOption[]
  const roles = useAppSelector(selectCurrentUserRoles) as unknown[]
  const canSeeAll = useAppSelector(state =>
    isAllowed(state, [inventoriesPermissions.seeAll]),
  )
  const canDownloadReport = useAppSelector(state =>
    isAllowed(state, [inventoriesPermissions.downloadReport]),
  )
  const canAdjustReserved = useAppSelector(state =>
    isAllowed(state, [inventoriesPermissions.adjustReserved]),
  )
  const loadingWarehouses = useAppSelector(state =>
    loadingSelector([actionTypes.GET_WAREHOUSE_LITE])(state),
  )

  const [warehouse, setWarehouse] = useState<WarehouseOption>(DEFAULT_WAREHOUSE)
  const [params, setParams] = useState<InventoryExportParams>({})

  const {
    loading: loadingReport,
    exportInventory,
    exportDetailed,
    exportManagement,
    exportManagementSummary,
  } = useInventoryExport(warehouse.value, params)

  const reserved = useReservedInventory(warehouse.value, params)

  // Reset de categorías locales al montar.
  useEffect(() => {
    dispatch(onSetLocalCategories([]))
  }, [dispatch])

  // Carga las bodegas una sola vez, cuando los roles del usuario ya llegaron.
  const warehousesRequested = useRef(false)
  useEffect(() => {
    if (warehousesRequested.current || roles.length === 0) return
    warehousesRequested.current = true
    dispatch(getWarehouseLite({ all: canSeeAll }, true))
  }, [roles.length, canSeeAll, dispatch])

  // Selecciona una bodega por defecto cuando el listado termina de cargar.
  useEffect(() => {
    if (warehouse.value !== null) return
    const preselected = ownWarehouses[0] || allWarehouses[0]
    if (preselected) setWarehouse(preselected)
  }, [ownWarehouses, allWarehouses, warehouse.value])

  const dropdownItems = useMemo(() => {
    const items: any[] = []
    if (canDownloadReport) {
      items.push(
        {
          title: 'Exportar inventario',
          tooltip: 'Exporta un excel con el inventario',
          action: exportInventory,
          icon: faFileExcel,
          iconColor: 'green',
        },
        {
          title: 'Exportar inventario desglosado por producto',
          tooltip: 'Exporta un excel con el inventario desglosado por producto',
          action: exportDetailed,
          icon: faFileExcel,
          iconColor: 'green',
        },
        {
          title: 'Exportar reporte de gestión de inventario',
          tooltip: 'Exporta un excel con el reporte de gestión de inventario por bodega',
          action: exportManagement,
          icon: faFileExcel,
          iconColor: 'green',
        },
        {
          title: 'Exportar reporte de gestión resumido por presentación',
          tooltip:
            'Exporta un excel con el reporte de gestión de inventario resumido: una fila por presentación de producto, sin desglose por lote',
          action: exportManagementSummary,
          icon: faFileExcel,
          iconColor: 'green',
        },
      )
    }
    if (canAdjustReserved) {
      items.push({
        title: 'Ajustar inventario reservado',
        tooltip: 'Libera o elimina el inventario reservado de la bodega',
        action: reserved.openModal,
        icon: faBoxOpen,
        iconColor: 'orange',
        divide: canDownloadReport,
      })
    }
    return items
  }, [
    canDownloadReport,
    canAdjustReserved,
    exportInventory,
    exportDetailed,
    exportManagement,
    exportManagementSummary,
    reserved.openModal,
  ])

  return (
    <div>
      <Title title={'Inventario'} />
      <Row>
        <Col>
          <Card
            title={'Listado de inventario'}
            white
            button={
              <Row className={'container-buttons'} style={{ alignItems: 'center' }}>
                {dropdownItems.length > 0 && (
                  <Dropdown
                    style={{ textTransform: 'none' }}
                    tooltip={'Acciones'}
                    loading={loadingReport}
                    items={dropdownItems}
                  />
                )}
              </Row>
            }>
            {allWarehouses.length === 0 && (
              <h3 style={{ margin: '0 auto' }}>No tiene ninguna bodega asignada</h3>
            )}
            <Row>
              <Col xl={12} lg={12} md={12} sm={12} xs={12}>
                <Select
                  label={'Bodegas'}
                  loading={loadingWarehouses}
                  options={allWarehouses}
                  value={warehouse}
                  onChange={(data: WarehouseOption) => setWarehouse(data)}
                />
              </Col>
              <Col xl={12} lg={12} md={12} sm={12} xs={12}>
                <WarehouseInventory
                  id={warehouse.value}
                  isInventory
                  onChangeParams={(next: InventoryExportParams) => setParams(next)}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <ReservedInventoryModal
        open={reserved.open}
        items={reserved.items}
        loadingList={reserved.loadingList}
        submitting={reserved.submitting}
        onRelease={reserved.onRelease}
        onRemove={reserved.onRemove}
        onClose={reserved.closeModal}
      />
    </div>
  )
}

export default Inventory
